import React, { useState, useEffect } from 'react'
import './Blog.css'
import BlogSubscribeForm from '../SubscribeFormFolder/BlogSubscribeForm'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Auth';

import {
  LazyLoadImage,
  LazyLoadComponent,
} from 'react-lazy-load-image-component'

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { CiShare2 } from "react-icons/ci"

// Assets
const blogBanner = require('./img/blog-banner.png')

// Interface for reading image
interface Blogs {
  postID: number;
  link_name: string;
  title: string;
  author: string;
  summary: string;
  tags: string;
  date: string;   // string because it comes from API
  mainImage: any;
  draft: boolean;
}

export function displayNotification() {
  toast.success("Link copied to your clipboard!", {
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    style: {
      backgroundColor: '#c3c9cfff',
      color: 'black',
      fontSize: '16px',
    }
  });
}

const BlogCard = ({
  postID,
  link_name,
  image,
  tags,
  title,
  author,
  date,
  content,
  displayNotification,
}: {
  postID: number;
  link_name: string;
  image: string;
  tags: string[];
  title: string;
  author: string;
  date: string;
  content: string;
  displayNotification: () => void;
}) => {
  const navigate = useNavigate();

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = link_name
      ? `${window.location.origin}/fountainn/blog/${link_name}`
      : `${window.location.origin}/fountainn/post/${postID}`;

    navigator.clipboard.writeText(url)
      .then(() => displayNotification())
      .catch(err => console.error("Failed to copy: ", err));
  };

  return (
    <div
      className="blog-card"
      onClick={() => navigate(
        link_name
          ? `/fountainn/blog/${link_name}`
          : `/fountainn/post/${postID}`
      )}
    >
      <div className='blog-card-img-with-tags'>
        <img src={image} alt="Blog" className="blog-card-img" />
        <div className="dark-overlay"></div>
        <div className='blog-tags'>
          {tags.map((tag, i) => (
            <div className='blog-tag' key={i}>{tag}</div>
          ))}
        </div>
        <div className="blog-share" onClick={handleShareClick}>
          <CiShare2 className='share-icon' />
        </div>
      </div>

      <div className='blog-card-title-and-content'>
        <h1>{title}</h1>
        <div className='blog-credentials'>
          <p style={{ color: '#000' }}>{author}</p>
          <p>━</p>
          <p className="blog-date">{date}</p>
        </div>
        <p style={{ color: '#6C757D' }}>{content}</p>
      </div>
    </div>
  );
};

const Blog = () => {
  const [blogs, setBlogs] = useState<Blogs[]>([]);
  const [visibleCount, setVisibleCount] = useState(6); // Show first 6 posts initially
  const { isAuthenticated, getPermissions } = useAuth();
  const [permissionGroups, setPermissionGroups] = useState<{ [key: string]: any }>({});

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      const permissions = await getPermissions();
      setPermissionGroups(permissions || {});
    };

    if (isAuthenticated) {
      fetchPermissions();
    }
  }, [isAuthenticated, getPermissions]);

  // Fetch blog posts
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch('/api/blogposts/', {
          method: 'GET',
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data: Blogs[] = await response.json();
        setBlogs(data);
      } catch (err) {
        if (err instanceof Error) {
          console.error(err.message);
        } else {
          console.error('An unknown error occurred');
        }
      }
    };

    fetchBlogs();
  }, []);

  // Apply filtering rules
  const filteredBlogs = blogs.filter(blog => {
    const now = new Date();

    // Hide future-dated posts UNLESS user is in Marketing
    if (blog.date && new Date(blog.date) > now) {
      if (permissionGroups?.Marketing || permissionGroups?.MarketingTeam)
        return true;
      else
        return false;
    }

    // Drafts only visible to Marketing team
    if (blog.draft) {
      if (permissionGroups?.Marketing || permissionGroups?.MarketingTeam)
        return true;
      else
        return false;
    }

    // Otherwise, show the blog
    return true;
  });


  // Slice blogs to show only `visibleCount` posts
  const visibleBlogs = filteredBlogs.slice(0, visibleCount);

  // Handler for Load More button
  const handleLoadMore = () => {
    setVisibleCount(filteredBlogs.length); // Show all remaining posts
  };

  return (
    <div className="home-container">
      <ToastContainer />

      <div className="blog-hero-section">
        <LazyLoadComponent placeholder={<img src={blogBanner} alt="" />}>
          <LazyLoadImage src={blogBanner} alt="Banner" className="hero-image3" />
        </LazyLoadComponent>
        <div className="dark-overlay"></div>
        <div className="news-info-wrapper">
          <h1>FountaiNN</h1>
        </div>
      </div>

      <div className='blog-hero-section2'>
        <p>A resource hub offering diverse information on FNN™’s core topics. Here, we share a wide range of
          insightful blog posts that dive into key areas related to FNN™’s mission, technologies, and community.</p>
      </div>

      <div className='blog-card-wrapper'>
        <h1>Explore the FountaiNN</h1>
        <div className='blog-card-stack'>
          {visibleBlogs.map(blog => (
            <BlogCard
              key={blog.postID}
              postID={blog.postID}
              link_name={(blog.link_name)}
              image={blog.mainImage}
              tags={blog.tags.split(',').map(tag => tag.trim())}
              title={blog.title}
              author={blog.author}
              date={blog.date ? new Date(blog.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ""}
              content={blog.summary}
              displayNotification={displayNotification}
            />
          ))}
        </div>

        {/* Show Load More button only if there are more than 6 posts */}
        {filteredBlogs.length > 6 && visibleCount < filteredBlogs.length && (
          <button className='load-more-blogs-button' onClick={handleLoadMore}>
            Load More
          </button>
        )}
      </div>

      <BlogSubscribeForm />
    </div>
  )
}


export default Blog;
